import { parseCurrencyInput, parseDecimalInput, parseIntegerInput, type InventoryItemSnapshot, type QuoteLineKind, type Repair, type RepairStatus } from '@ck/domain'

export interface PartDraft {
  id: string | null
  code: string
  description: string
  quantity: string
  unitPrice: string
}

export interface LaborDraft {
  id: string | null
  description: string
  hours: string
  hourlyRate: string
  technicianSubject: string | null
}

export interface RepairQuoteLineDraft {
  id: string | null
  kind: QuoteLineKind
  description: string
  quantity: string
  unitPrice: string
  inventoryItemId: string | null
  inventoryLotId: string | null
  inventorySnapshot: InventoryItemSnapshot | null
}

export interface RepairDraft {
  id: string | null
  stageId: string
  title: string
  description: string
  status: RepairStatus
  version: number | null
  lines: RepairQuoteLineDraft[]
}

export function validateWorkOrderEditLines(parts: PartDraft[], labor: LaborDraft[]): string | null {
  const invalidPart = parts.some((part) => {
    const quantity = parseIntegerInput(part.quantity)
    const unitPrice = parseCurrencyInput(part.unitPrice)
    return !part.code.trim() || !part.description.trim() || quantity === null || quantity <= 0 || unitPrice === null || unitPrice < 0
  })
  if (invalidPart) return 'Preencha código, descrição, quantidade inteira e valor unitário válidos em todas as peças.'

  const invalidLabor = labor.some((entry) => {
    const hours = parseDecimalInput(entry.hours)
    const hourlyRate = parseCurrencyInput(entry.hourlyRate)
    return !entry.description.trim() || hours === null || hours <= 0 || hourlyRate === null || hourlyRate < 0
  })
  if (invalidLabor) return 'Preencha descrição, horas e valor por hora válidos em toda a mão de obra.'
  return null
}

export function emptyRepairLine(kind: QuoteLineKind = 'part'): RepairQuoteLineDraft {
  return {
    id: null,
    kind,
    description: '',
    quantity: '1',
    unitPrice: '',
    inventoryItemId: null,
    inventoryLotId: null,
    inventorySnapshot: null
  }
}

export function repairDraftFromRecord(repair: Repair): RepairDraft {
  return {
    id: repair.id,
    stageId: repair.stageExecutionId,
    title: repair.title,
    description: repair.description ?? '',
    status: repair.status,
    version: repair.version,
    lines: repair.quoteLines.map((line) => ({
      id: line.id,
      kind: line.kind,
      description: line.description,
      quantity: String(line.quantity).replace('.', ','),
      unitPrice: line.unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      inventoryItemId: line.inventoryItemId ?? null,
      inventoryLotId: line.inventoryLotId ?? null,
      inventorySnapshot: line.inventorySnapshot ?? null
    }))
  }
}

export function emptyRepairDraft(stageId: string): RepairDraft {
  return {
    id: null,
    stageId,
    title: '',
    description: '',
    status: 'draft',
    version: null,
    lines: [emptyRepairLine()]
  }
}

export function validateRepairDrafts(repairs: RepairDraft[]): string | null {
  for (const repair of repairs) {
    if (!repair.stageId) return 'Selecione a etapa do checklist de cada reparo.'
    if (repair.title.trim().length < 2) return 'Informe um título válido para cada reparo.'
    if (repair.lines.length === 0) return 'Adicione ao menos uma linha em cada orçamento.'
    for (const line of repair.lines) {
      const quantity = line.kind === 'part' ? parseIntegerInput(line.quantity) : parseDecimalInput(line.quantity)
      const unitPrice = parseCurrencyInput(line.unitPrice)
      if (!line.description.trim()) return 'Descreva todas as linhas dos reparos.'
      if (line.kind === 'part' && /[.,]/.test(line.quantity)) return 'A quantidade de peças deve ser inteira.'
      if (quantity === null || quantity <= 0 || unitPrice === null || unitPrice < 0) return 'Informe quantidades e valores válidos nos reparos.'
      if (line.kind !== 'part' && (line.inventoryItemId || line.inventorySnapshot)) return 'Estoque só pode ser usado em linhas de peça.'
      if ((line.inventoryItemId && !line.inventorySnapshot) || (!line.inventoryItemId && line.inventorySnapshot)) return 'Selecione novamente a peça do estoque.'
    }
  }
  return null
}
